import { useEffect, useLayoutEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router";
import { applyBillingFilterParams, parseBillingFilterParams } from "@/lib/billing-filter-url";
import { useBillingFilterStore } from "@/stores/useBillingFilterStore";

/** 課金ダッシュボードのルート。ここ以外にいる間はクエリを読み書きしない。 */
const BILLING_PATHNAME = "/billing";

/**
 * 課金ダッシュボードの絞り込みを URL クエリと同期する。
 *
 * - マウント時、URL に絞り込みがあればそれをストアへ復元する (再読み込み・ディープリンク)。
 *   無ければ手元の選択をそのまま URL へ書き出す (他ビューから戻ったとき)。
 * - 以後は絞り込みが変わるたびに URL を置き換える (履歴は増やさない)。
 *
 * 何も描画しない。`BillingPage` の中にマウントする。
 */
export function BillingFilterUrlSync() {
	const { pathname } = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	const period = useBillingFilterStore((s) => s.period);
	const categoryIds = useBillingFilterStore((s) => s.categoryIds);
	const organizationId = useBillingFilterStore((s) => s.organizationId);
	const includeArchived = useBillingFilterStore((s) => s.includeArchived);

	// 復元が済むまでストア → URL の書き戻しを止め、復元前の既定値で URL を潰さない。
	const [restored, setRestored] = useState(false);

	// URL → ストア。復元は初回だけで、以後は URL 側をストアに追従させる。
	useLayoutEffect(() => {
		if (restored) return;
		setRestored(true);
		const urlState = parseBillingFilterParams(searchParams);
		if (urlState === null) return;
		const store = useBillingFilterStore.getState();
		store.setPeriod(urlState.period);
		store.setCategoryIds(urlState.categoryIds);
		// 同じ組織なら手元の表示名を保ち、違えば OrganizationSelector が一覧の取得後に補う。
		store.setOrganization(
			urlState.organizationId,
			urlState.organizationId === store.organizationId ? store.organizationName : "",
		);
		// URL に archived が無ければ undefined。localStorage 永続化済みの好みを保つため触らない。
		if (urlState.includeArchived !== undefined) {
			store.setIncludeArchived(urlState.includeArchived);
		}
	}, [restored, searchParams]);

	// ストア → URL。他ビューへ移った直後にも searchParams の変化でここが走るため、
	// 課金のパスにいる間だけ書き込んで別ルートの URL を汚さない。
	useEffect(() => {
		if (!restored || pathname !== BILLING_PATHNAME) return;
		const next = applyBillingFilterParams(searchParams, {
			period,
			categoryIds,
			organizationId,
			includeArchived,
		});
		if (next.toString() === searchParams.toString()) return;
		setSearchParams(next, { replace: true });
	}, [
		restored,
		pathname,
		period,
		categoryIds,
		organizationId,
		includeArchived,
		searchParams,
		setSearchParams,
	]);

	return null;
}
